/**
 * Сервис для управления прогрессом студента (tartib и mashqtartib)
 */

const XOLAT_API = "https://apitr.subxonqulov.uz/api/xolat/";

// Кэш инициализаций в памяти для предотвращения дублирования
// Ключ: "fanId:userId", Значение: Promise
const initializationCache = new Map();

/**
 * Получить заголовки авторизации
 */
function buildAuthHeaders() {
  const headers = { "Content-Type": "application/json" };
  try {
    let token = null;
    token = localStorage.getItem("auth:token") ||
            localStorage.getItem("auth:access") ||
            localStorage.getItem("auth:key") ||
            localStorage.getItem("auth:jwt") ||
            localStorage.getItem("auth:auth_token");

    if (token) {
      const isJwt = token.startsWith("eyJ");
      headers["Authorization"] = isJwt ? `Bearer ${token}` : `Token ${token}`;
    }
  } catch (e) {
    console.warn("Error building auth headers:", e);
  }
  return headers;
}

/**
 * Получить текущего пользователя
 */
export function getCurrentUser() {
  try {
    // Сначала проверяем localStorage, затем sessionStorage
    let userJson = localStorage.getItem("auth:user");
    if (!userJson) {
      userJson = sessionStorage.getItem("auth:user");
    }
    
    const u = JSON.parse(userJson || "null");
    const userId = u?.id ?? null;
    
    if (!userId) {
      console.warn("getCurrentUser: No user found in localStorage or sessionStorage");
    } else {
      console.log("getCurrentUser: Found user with id:", userId);
    }
    
    return userId;
  } catch (e) {
    console.warn("getCurrentUser: Error parsing user:", e);
    return null;
  }
}

/**
 * Получить прогресс студента для конкретного предмета
 * @param {number|string} fanId - ID предмета
 * @param {number|string} userId - ID пользователя
 * @returns {Promise<Object|null>} объект прогресса или null
 */
export async function getStudentProgress(fanId, userId) {
  try {
    const url = `${XOLAT_API}?fan=${fanId}&user=${userId}`;
    const res = await fetch(url, { headers: buildAuthHeaders() });
    if (!res.ok) return null;
    
    const data = await res.json();
    // API возвращает массив, берём первый элемент
    return Array.isArray(data) && data.length > 0 ? data[0] : null;
  } catch (e) {
    console.warn("Error fetching progress:", e);
    return null;
  }
}

/**
 * Инициализировать прогресс студента (создать запись, если её нет)
 * Использует глобальный кэш и тройную проверку чтобы избежать race conditions
 * @param {number|string} fanId - ID предмета
 * @param {number|string} userId - ID пользователя
 * @returns {Promise<Object|null>} созданный или существующий объект прогресса
 */
export async function initializeProgress(fanId, userId) {
  try {
    if (!fanId || !userId) {
      console.warn("initializeProgress called with missing fanId or userId");
      return null;
    }

    const cacheKey = `${fanId}:${userId}`;
    
    // Если уже есть в полёте запрос на инициализацию - вернуть его Promise
    if (initializationCache.has(cacheKey)) {
      console.log("Initiative already in flight for:", cacheKey);
      return initializationCache.get(cacheKey);
    }

    // Создаём Promise для этой инициализации
    const initPromise = (async () => {
      try {
        // Первая проверка: есть ли уже запись
        console.log("First check for existing progress:", cacheKey);
        const existing = await getStudentProgress(fanId, userId);
        if (existing) {
          console.log("Progress already exists:", existing.id);
          return existing;
        }

        console.log("Creating new progress for fan:", fanId, "user:", userId);

        // Небольшая задержка для синхронизации других одновременных запросов
        await new Promise(r => setTimeout(r, 150));
        
        // Вторая проверка перед созданием (может быть создано другим процессом)
        console.log("Second check before creation:", cacheKey);
        const checkAgain = await getStudentProgress(fanId, userId);
        if (checkAgain) {
          console.log("Progress was already created by another request:", checkAgain.id);
          return checkAgain;
        }

        // Создаём новую запись
        console.log("Sending POST request to create progress");
        const res = await fetch(XOLAT_API, {
          method: "POST",
          headers: buildAuthHeaders(),
          body: JSON.stringify({
            fan: fanId,
            user: userId,
            tartib: 1,
            mashqtartib: 0,
          }),
        });

        if (!res.ok) {
          console.warn("Failed to create progress:", res.status, res.statusText);
          
          // Третья проверка при ошибке (может быть создано параллельным запросом)
          if (res.status === 400 || res.status === 409 || res.status === 500) {
            console.log("Got error response, checking if created by concurrent request");
            await new Promise(r => setTimeout(r, 200));
            const checkThird = await getStudentProgress(fanId, userId);
            if (checkThird) {
              console.log("Progress was created by concurrent request:", checkThird.id);
              return checkThird;
            }
          }
          return null;
        }

        const created = await res.json().catch(() => null);
        console.log("Successfully created progress with id:", created?.id);
        return created;
      } catch (e) {
        console.warn("Error in initializeProgress:", e);
        return null;
      } finally {
        // Удаляем из кэша после завершения (успешно или с ошибкой)
        initializationCache.delete(cacheKey);
      }
    })();

    // Сохраняем Promise в кэш
    initializationCache.set(cacheKey, initPromise);
    
    return await initPromise;
  } catch (e) {
    console.warn("Error initializing progress:", e);
    const cacheKey = `${fanId}:${userId}`;
    initializationCache.delete(cacheKey);
    return null;
  }
}

/**
 * Обновить tartib (текущий номер урока)
 * @param {number|string} progressId - ID записи прогресса
 * @param {number} newTartib - новое значение tartib
 */
export async function updateTartib(progressId, newTartib) {
  try {
    const url = `${XOLAT_API}${progressId}/`;
    const res = await fetch(url, {
      method: "PATCH",
      headers: buildAuthHeaders(),
      body: JSON.stringify({ tartib: newTartib }),
    });

    if (!res.ok) {
      console.warn("Failed to update tartib:", res.status);
      return null;
    }

    return await res.json().catch(() => null);
  } catch (e) {
    console.warn("Error updating tartib:", e);
    return null;
  }
}

/**
 * Обновить mashqtartib (текущий номер контрольной работы)
 * @param {number|string} progressId - ID записи прогресса
 * @param {number} newMashqTartib - новое значение mashqtartib
 */
export async function updateMashqTartib(progressId, newMashqTartib) {
  try {
    const url = `${XOLAT_API}${progressId}/`;
    const res = await fetch(url, {
      method: "PATCH",
      headers: buildAuthHeaders(),
      body: JSON.stringify({ mashqtartib: newMashqTartib }),
    });

    if (!res.ok) {
      console.warn("Failed to update mashqtartib:", res.status);
      return null;
    }

    return await res.json().catch(() => null);
  } catch (e) {
    console.warn("Error updating mashqtartib:", e);
    return null;
  }
}

/**
 * Определить, доступен ли урок для студента
 * @param {number|string} lessonTartib - номер урока (tartib)
 * @param {number|string} studentTartib - текущий tartib студента
 * @returns {boolean}
 */
export function isLessonAvailable(lessonTartib, studentTartib) {
  // Урок доступен, если его номер <= текущему tartib студента
  return Number(lessonTartib) <= Number(studentTartib);
}

/**
 * Определить, доступна ли контрольная работа для студента
 * @param {number|string} testMashqTartib - номер контрольной (mashqtartib)
 * @param {number|string} studentMashqTartib - текущий mashqtartib студента
 * @param {number|string} studentTartib - текущий tartib студента (все уроки перед контрольной должны быть пройдены)
 * @param {Array} allLessons - все уроки
 * @returns {boolean}
 */
export function isTestAvailable(testMashqTartib, studentMashqTartib, studentTartib, allLessons = []) {
  // Контрольная доступна, если:
  // 1. Номер контрольной <= текущему mashqtartib студента
  // 2. Все уроки перед контрольной пройдены (все уроки с tartib <= studentTartib)
  
  const testNum = Number(testMashqTartib);
  const studentTestNum = Number(studentMashqTartib);
  
  if (testNum > studentTestNum) {
    return false;
  }

  // Проверяем, что пройдены все уроки перед этой контрольной
  // (это происходит, когда tartib достаточно высокий)
  return true;
}

/**
 * Определить статус урока/контрольной для студента
 * 
 * Логика: Элементы группируются по tartib (Урок 1 + Контрольная 1 = tartib=1, и т.д.)
 * Доступный только первый элемент текущей группы tartib
 * 
 * Если контрольная заблокирована (test_blocked=true), она остается заблокированной
 * пока студент не пройдет ВСЕ уроки блока (tartib должен быть > tartib контрольной)
 * 
 * @param {Object} lesson - объект урока/контрольной
 * @param {Object} progressData - данные прогресса студента
 * @param {Array} allLessons - массив всех уроков и контрольных (по порядку)
 * @returns {string} "available", "completed", или "locked"
 */
export function getLessonStatus(lesson, progressData, allLessons = []) {
  if (!progressData) {
    console.warn("No progressData provided for lesson:", lesson.id);
    return "locked";
  }

  const studentTartib = Number(progressData.tartib ?? 1);
  const lessonTartib = Number(lesson.tartib ?? 0);
  const isTest = lesson.type === "test";

  // Если контрольная заблокирована, она должна оставаться заблокированной
  // пока студент не пройдет ВСЕ уроки блока (tartib > tartib контрольной)
  if (isTest && progressData.test_blocked) {
    console.log(`Test ${lesson.id} is blocked. studentTartib=${studentTartib}, testTartib=${lessonTartib}. Unlocking only if tartib > testTartib`);
    
    // Контрольная разблокируется только когда tartib > tartib контрольной
    // (т.е. когда пройдены ВСЕ уроки ПЕРЕД этой контрольной)
    if (studentTartib > lessonTartib) {
      // Уроки блока пройдены - контрольная разблокируется
      return "available";
    } else {
      // Уроки блока еще не пройдены - контрольная остается заблокированной
      return "locked";
    }
  }

  // Если нет массива всех уроков, используем старую логику
  if (!allLessons || allLessons.length === 0) {
    console.warn("No allLessons array provided, using fallback logic");
    
    if (lessonTartib < studentTartib) {
      return "completed";
    } else if (lessonTartib === studentTartib) {
      return "available";
    } else {
      return "locked";
    }
  }

  // Новая логика: используем tartib для группировки
  // Элементы с одинаковым tartib - это одна группа (например, Урок 1 + Контрольная 1)
  
  if (lessonTartib < studentTartib) {
    // Все элементы с tartib меньше studentTartib - пройдены
    return "completed";
  } else if (lessonTartib === studentTartib) {
    // Элементы с tartib === studentTartib - доступны, но только первый в группе
    // Найдём позицию текущего элемента в группе элементов с одинаковым tartib
    const sameGroupElements = allLessons.filter(l => Number(l.tartib ?? 0) === lessonTartib);
    const positionInGroup = sameGroupElements.findIndex(l => String(l.id) === String(lesson.id));
    
    console.log(`Lesson ${lesson.id}: tartib=${lessonTartib}, student tartib=${studentTartib}, position in group=${positionInGroup}, group size=${sameGroupElements.length}`);
    
    // Доступен только первый элемент в группе (positionInGroup === 0)
    return positionInGroup === 0 ? "available" : "locked";
  } else {
    // Все элементы с tartib больше studentTartib - заблокированы
    return "locked";
  }
}

/**
 * Получить количество попыток для контрольной работы
 * @param {number|string} progressId - ID записи прогресса
 * @returns {Promise<number>} количество попыток
 */
export async function getTestAttempts(progressId) {
  try {
    const progress = await fetch(`${XOLAT_API}${progressId}/`, {
      headers: buildAuthHeaders()
    }).then(r => r.ok ? r.json() : null);
    
    return progress?.test_attempts_count ?? 0;
  } catch (e) {
    console.warn("Error getting test attempts:", e);
    return 0;
  }
}

/**
 * Увеличить количество попыток для контрольной
 * @param {number|string} progressId - ID записи прогресса
 * @param {number|string} testId - ID контрольной работы
 * @returns {Promise<Object|null>} обновленные данные прогресса
 */
export async function incrementTestAttempts(progressId, testId) {
  try {
    // Получаем текущие данные
    const progress = await fetch(`${XOLAT_API}${progressId}/`, {
      headers: buildAuthHeaders()
    }).then(r => r.ok ? r.json() : null);
    
    if (!progress) return null;
    
    const newAttempts = (progress.test_attempts_count ?? 0) + 1;
    
    console.log(`Incrementing test attempts for test ${testId}:`, {
      currentAttempts: progress.test_attempts_count,
      newAttempts: newAttempts
    });
    
    // Обновляем счетчик попыток
    const updated = await fetch(`${XOLAT_API}${progressId}/`, {
      method: "PATCH",
      headers: buildAuthHeaders(),
      body: JSON.stringify({
        test_attempts_count: newAttempts,
        current_test_id: testId
      })
    }).then(r => r.ok ? r.json() : null);
    
    return updated;
  } catch (e) {
    console.warn("Error incrementing test attempts:", e);
    return null;
  }
}

/**
 * Заблокировать контрольную (после 3 неудачных попыток)
 * @param {number|string} progressId - ID записи прогресса
 * @param {boolean} blocked - статус блокировки
 * @returns {Promise<Object|null>} обновленные данные прогресса
 */
export async function setTestBlocked(progressId, blocked = true) {
  try {
    const updated = await fetch(`${XOLAT_API}${progressId}/`, {
      method: "PATCH",
      headers: buildAuthHeaders(),
      body: JSON.stringify({
        test_blocked: blocked,
        test_attempts_count: blocked ? 3 : 0
      })
    }).then(r => r.ok ? r.json() : null);
    
    return updated;
  } catch (e) {
    console.warn("Error setting test blocked status:", e);
    return null;
  }
}

/**
 * Сбросить счетчик попыток контрольной (после перепрохождения уроков)
 * @param {number|string} progressId - ID записи прогресса
 * @returns {Promise<Object|null>} обновленные данные прогресса
 */
export async function resetTestAttempts(progressId) {
  try {
    const updated = await fetch(`${XOLAT_API}${progressId}/`, {
      method: "PATCH",
      headers: buildAuthHeaders(),
      body: JSON.stringify({
        test_attempts_count: 0,
        test_blocked: false
      })
    }).then(r => r.ok ? r.json() : null);
    
    return updated;
  } catch (e) {
    console.warn("Error resetting test attempts:", e);
    return null;
  }
}

/**
 * Найти первый урок перед контрольной (начало блока)
 * @param {Array} allLessons - массив всех уроков и контрольных
 * @param {number|string} testTartib - tartib контрольной
 * @returns {Object|null} первый урок перед контрольной или null
 */
export function findFirstLessonBeforeTest(allLessons, testTartib) {
  const testTartibNum = Number(testTartib);
  
  // Находим все элементы с tartib < testTartib и типом "lesson"
  const lessonsBeforeTest = allLessons.filter(
    l => Number(l.tartib ?? 0) < testTartibNum && l.type === "lesson"
  );
  
  if (lessonsBeforeTest.length === 0) return null;
  
  // Возвращаем первый элемент (с наименьшим tartib)
  return lessonsBeforeTest.sort((a, b) => 
    Number(a.tartib ?? 0) - Number(b.tartib ?? 0)
  )[0];
}

/**
 * Найти tartib начала блока (все уроки перед контрольной)
 * @param {Array} allLessons - массив всех уроков и контрольных
 * @param {number|string} testTartib - tartib контрольной
 * @returns {number} tartib начала блока
 */
export function findBlockStartTartib(allLessons, testTartib) {
  const testTartibNum = Number(testTartib);
  
  // Находим все элементы с tartib < testTartib и типом "lesson"
  const lessonsBeforeTest = allLessons.filter(
    l => Number(l.tartib ?? 0) < testTartibNum && l.type === "lesson"
  );
  
  if (lessonsBeforeTest.length === 0) return testTartibNum;
  
  // Возвращаем tartib первого урока
  const firstLesson = lessonsBeforeTest.sort((a, b) => 
    Number(a.tartib ?? 0) - Number(b.tartib ?? 0)
  )[0];
  
  return Number(firstLesson.tartib ?? 0);
}

/**
 * Откатить прогресс студента к началу блока при неудаче контрольной
 * @param {number|string} progressId - ID записи прогресса
 * @param {Array} allLessons - массив всех уроков и контрольных
 * @param {number|string} testTartib - tartib контрольной, на которой студент не прошел
 * @returns {Promise<Object|null>} обновленные данные прогресса
 */
export async function revertProgressToBlockStart(progressId, allLessons, testTartib) {
  try {
    const blockStartTartib = findBlockStartTartib(allLessons, testTartib);
    
    console.log("Reverting progress to block start:", {
      testTartib: testTartib,
      blockStartTartib: blockStartTartib
    });
    
    // Откатываем tartib к началу блока
    const updated = await fetch(`${XOLAT_API}${progressId}/`, {
      method: "PATCH",
      headers: buildAuthHeaders(),
      body: JSON.stringify({
        tartib: blockStartTartib,
        test_attempts_count: 0  // Сбрасываем счетчик попыток
      })
    }).then(r => r.ok ? r.json() : null);
    
    return updated;
  } catch (e) {
    console.warn("Error reverting progress:", e);
    return null;
  }
}
